import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { motion as Motion, AnimatePresence } from 'framer-motion';

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchCollection('gallery');
      setItems(data);
      setLoading(false);
    };
    loadData();
  }, []);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  }, [items.length]);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    let interval;
    if (isAutoPlaying && items.length > 0) {
      interval = setInterval(nextSlide, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, items.length]);

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.9,
    }),
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Ingen billeder fundet</h2>
          <p className="text-slate-400">Det ser ud til, at fotoalbummet er tomt lige nu.</p>
        </div>
      </div>
    );
  }

  const currentItem = items[currentIndex];

  return (
    <>
      <Helmet>
        <title>Interaktivt Galleri | LimfjordsOdderen</title>
        <meta name="description" content="Oplev mine eventyr i et interaktivt slideshow. Se billeder af mig og mine venner i Limfjorden." />
      </Helmet>

      <div className="min-h-screen bg-slate-950 pt-24 pb-12 px-4 flex flex-col items-center justify-center overflow-hidden">
        <div className="max-w-7xl w-full mx-auto">
          <div className="text-center mb-10">
            <Motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight"
            >
              Mine <span className="text-blue-400">Vilde</span> Eventyr
            </Motion.h1>
            <Motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto"
            >
              Bladr igennem mine yndlingsøjeblikke og se hvem jeg har mødt på min vej.
            </Motion.p>
          </div>

          <div className="relative aspect-video md:aspect-[21/9] w-full max-w-6xl mx-auto group">
            {/* Main Image Container */}
            <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/20 border border-white/10">
              <AnimatePresence initial={false} custom={direction}>
                <Motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.4 },
                    scale: { duration: 0.4 }
                  }}
                  className="absolute inset-0"
                >
                  <img
                    src={currentItem.image[0]}
                    alt={currentItem.title.da}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  
                  {/* Content Overlay */}
                  <Motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white"
                  >
                    <h2 className="text-3xl md:text-5xl font-bold mb-4">{currentItem.title.da}</h2>
                    <p className="text-lg md:text-xl text-slate-200 max-w-3xl leading-relaxed">
                      {currentItem.description.da}
                    </p>
                  </Motion.div>
                </Motion.div>
              </AnimatePresence>

              {/* Navigation Controls */}
              <div className="absolute inset-0 flex items-center justify-between p-4 z-20 pointer-events-none">
                <button
                  onClick={prevSlide}
                  className="pointer-events-auto p-4 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-blue-500 transition-all duration-300 border border-white/20 shadow-xl group/btn"
                  aria-label="Forrige billede"
                >
                  <ChevronLeft className="h-8 w-8 group-hover/btn:-translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={nextSlide}
                  className="pointer-events-auto p-4 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-blue-500 transition-all duration-300 border border-white/20 shadow-xl group/btn"
                  aria-label="Næste billede"
                >
                  <ChevronRight className="h-8 w-8 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* AutoPlay Toggle */}
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="absolute top-6 right-6 p-3 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-black/60 transition-colors border border-white/10 z-10"
              >
                {isAutoPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </button>
            </div>

            {/* Thumbnails / Progress Dots */}
            <div className="flex justify-center mt-8 gap-3 flex-wrap">
              {items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    index === currentIndex 
                      ? 'w-12 bg-blue-500' 
                      : 'w-2 bg-slate-700 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Grid Preview (Optional, for quick selection) */}
          <div className="mt-20 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {items.map((item, index) => (
              <Motion.button
                key={item.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-colors ${
                  index === currentIndex ? 'border-blue-500' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img 
                  src={item.image[0]} 
                  alt={item.title.da}
                  className="w-full h-full object-cover"
                />
              </Motion.button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Gallery;
